import "./index.css";
import { Dashboard } from "./components/Dashboard";
import { SignIn } from "./components/sign-in";
import { useConvexAuth } from "convex/react";
import { SimpleLoader } from "./components/ui/simple-loader";
import { Toaster } from "./components/ui/sonner";
import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import { useMountEffect } from "./hooks/use-mount-effect";

export function App() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const [location] = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    if (location === "/" || location === "/signin") {
      return <Redirect to="/dashboard" />;
    }
    return <DashboardScreen />;
  }

  if (location !== "/" && location !== "/signin") {
    return <Redirect to="/signin" />;
  }

  return <SignInScreen />;
}

// fixed fine-art hero for sign-in (single image, no rotation)
const SIGN_IN_ART_IMAGE =
  "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?q=85&w=2400&auto=format&fit=crop";

// ---------------------------------------------------------------------------
// sign-in hero quote (quotable.io random; fallback if fetch fails)
// ---------------------------------------------------------------------------

type SignInHeroQuote = {
  content: string;
  author: string;
};

/** last-resort pool when remote APIs are blocked or fail (still varies per load) */
const SIGN_IN_QUOTE_OFFLINE_POOL: readonly SignInHeroQuote[] = [
  {
    content:
      "Creativity is allowing yourself to make mistakes. Art is knowing which ones to keep.",
    author: "Scott Adams",
  },
  {
    content: "I paint objects as I think them, not as I see them.",
    author: "Pablo Picasso",
  },
  {
    content:
      "The richness I achieve comes from Nature, the source of my inspiration.",
    author: "Claude Monet",
  },
  {
    content: "Art is the only way to run away without leaving home.",
    author: "Twyla Tharp",
  },
  {
    content:
      "The purpose of art is washing the dust of daily life off our souls.",
    author: "Pablo Picasso",
  },
  {
    content: "Where the spirit does not work with the hand, there is no art.",
    author: "Leonardo da Vinci",
  },
  {
    content:
      "Art enables us to find ourselves and lose ourselves at the same time.",
    author: "Thomas Merton",
  },
  {
    content:
      "The world always seems brighter when you’ve made something that wasn’t there before.",
    author: "Neil Gaiman",
  },
];

function pickOfflineHeroQuote(): SignInHeroQuote {
  const r = Math.random();
  const idx = Math.floor(r * SIGN_IN_QUOTE_OFFLINE_POOL.length);
  const picked = SIGN_IN_QUOTE_OFFLINE_POOL[idx];
  if (picked) {
    return picked;
  }
  return SIGN_IN_QUOTE_OFFLINE_POOL[0];
}

function parseQuotableRandomPayload(data: unknown): SignInHeroQuote | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const record = data as Record<string, unknown>;
  const content = record["content"];
  const author = record["author"];
  if (typeof content !== "string" || typeof author !== "string") {
    return null;
  }
  if (content.length === 0 || author.length === 0) {
    return null;
  }
  return { content, author };
}

function parseZenRandomPayload(data: unknown): SignInHeroQuote | null {
  if (!Array.isArray(data) || data.length < 1) {
    return null;
  }
  const first = data[0];
  if (typeof first !== "object" || first === null) {
    return null;
  }
  const record = first as Record<string, unknown>;
  const q = record["q"];
  const a = record["a"];
  if (typeof q !== "string" || typeof a !== "string") {
    return null;
  }
  if (q.length === 0 || a.length === 0) {
    return null;
  }
  return { content: q, author: a };
}

function loadRandomHeroQuote(): Promise<SignInHeroQuote> {
  const quotableUrl = `https://api.quotable.io/random?maxLength=260&nonce=${String(Date.now())}`;
  return fetch(quotableUrl, { cache: "no-store" })
    .then((res) => {
      if (!res.ok) {
        return null;
      }
      return res.json().then((raw: unknown) => parseQuotableRandomPayload(raw));
    })
    .then((quote) => {
      if (quote !== null) {
        return quote;
      }
      const zenUrl = `https://zenquotes.io/api/random?cb=${String(Date.now())}`;
      return fetch(zenUrl, { cache: "no-store" }).then((res) => {
        if (!res.ok) {
          return null;
        }
        return res.json().then((raw: unknown) => parseZenRandomPayload(raw));
      });
    })
    .then((quote) => {
      if (quote !== null) {
        return quote;
      }
      return pickOfflineHeroQuote();
    })
    .catch(() => pickOfflineHeroQuote());
}

function LoadingScreen() {
  return (
    <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <SimpleLoader message="Authenticating..." />
      </div>
    </div>
  );
}

function SignInScreen() {
  const [heroQuote, setHeroQuote] = useState<SignInHeroQuote>(() =>
    pickOfflineHeroQuote(),
  );

  useMountEffect(() => {
    let active = true;
    loadRandomHeroQuote().then((quote) => {
      if (!active) {
        return;
      }
      setHeroQuote(quote);
    });
    return () => {
      active = false;
    };
  });

  return (
    <div className="fixed inset-0 flex w-screen overflow-hidden bg-zinc-100 lg:gap-6 lg:p-6">
      {/* left — fixed fine art, soft rounded frame (large screens only) */}
      <div className="relative hidden min-h-0 min-w-0 flex-1 overflow-hidden rounded-3xl bg-zinc-900 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.35)] ring-1 ring-zinc-900/20 lg:flex">
        <img
          src={SIGN_IN_ART_IMAGE}
          alt="Paul Klee–style abstract fine art painting with geometric color blocks"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/82 via-black/35 to-black/10" />

        <div className="relative z-10 flex h-full w-full flex-col justify-between p-8 text-white">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold tracking-tight text-white/90">
              Staff Portal
            </span>
          </div>
          <div className="space-y-3">
            <blockquote className="space-y-1.5" cite="https://api.quotable.io">
              <p className="text-lg font-medium leading-relaxed tracking-tight text-white/95 [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility]">
                “{heroQuote.content}”
              </p>
              <footer className="text-xs font-medium text-zinc-400 [-webkit-font-smoothing:antialiased]">
                — {heroQuote.author}
              </footer>
            </blockquote>
          </div>
        </div>
      </div>

      {/* right side — sign-in (no extra fills: same bg-zinc-100 as shell) */}
      <div className="relative flex min-w-0 flex-1 flex-col items-center justify-center overflow-hidden p-4 sm:p-8 lg:p-12">
        <div className="relative w-full min-w-0 max-w-sm space-y-6">
          <div className="relative isolate min-w-0 w-full max-w-full overflow-x-clip rounded-2xl border border-zinc-200/70 bg-white shadow-[0_24px_64px_-16px_rgba(9,9,11,0.14)]">
            <div
              aria-hidden
              className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-300/90 to-transparent"
            />
            <div className="relative min-w-0 max-w-full w-full ps-6 pe-6 pb-7 pt-8 sm:ps-8 sm:pe-8 sm:pb-8 sm:pt-9">
              <SignIn />
            </div>
          </div>

          <p className="min-w-0 px-2 text-center text-xs font-medium leading-relaxed tracking-wide text-zinc-500 [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility] [overflow-wrap:anywhere]">
            © {new Date().getFullYear()} Blirumah Property. All rights
            reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

function DashboardScreen() {
  return (
    <>
      <div className="h-screen w-screen overflow-hidden">
        <Dashboard />
      </div>
      <Toaster />
    </>
  );
}

export default App;
